import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors, breakpoints } from '../../../assets/styles/GlobalStyles';
import MainLayout from '../../../layouts/MainLayout';
import { useAuth } from '../../../contexts/AuthContext';
import { authService } from '../../../services';
import { getPedidosByDepartamento } from '../config';
import { formatCurrency } from '../core/services/pagamentoService';
import { getContextoAcesso, isPedidoVisivelParaUsuario } from '../core/services/acessoPedidoService';
import { FaFileAlt, FaArrowLeft, FaChevronRight, FaSearch, FaClipboardList, FaFileContract, FaUsers, FaGraduationCap } from 'react-icons/fa';

const PageContainer = styled.div`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);
`;

const Container = styled.div`
  padding: 120px 2rem 3rem 2rem;
  max-width: 1300px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 100px 1rem 2rem 1rem;
  }
`;

const TopBar = styled.div`
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: ${breakpoints.mobile}) {
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 1px solid #e1e4e8;
  border-radius: 12px;
  color: ${colors.darkBlue};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  padding: 0.6rem 1.2rem;
  transition: all 0.3s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  white-space: nowrap;
  
  &:hover {
    background: ${colors.lightBlue};
    border-color: ${colors.lightBlue};
    color: white;
    transform: translateX(-4px);
    box-shadow: 0 4px 12px rgba(37, 99, 128, 0.15);
  }
`;

const StatsBadge = styled.div`
  background: white;
  padding: 0.6rem 1.2rem;
  border-radius: 40px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  white-space: nowrap;
  
  span {
    font-size: 0.9rem;
    font-weight: 500;
    color: ${colors.darkBlue};
  }
  
  .count {
    background: ${colors.lightBlue};
    color: white;
    padding: 0.2rem 0.7rem;
    border-radius: 30px;
    font-size: 0.8rem;
    font-weight: 600;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 320px;
  animation: slideIn 0.3s ease;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.7rem 1rem 0.7rem 2.5rem;
  border: 1px solid #e1e4e8;
  border-radius: 12px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: white;

  // 16px evita o zoom automático do iOS ao focar
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 16px;
  }
  
  &:focus {
    outline: none;
    border-color: ${colors.lightBlue};
    box-shadow: 0 0 0 3px rgba(37, 99, 128, 0.1);
  }
  
  &::placeholder {
    color: #adb5bd;
  }
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 0.8rem;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  font-size: 0.9rem;
  pointer-events: none;
`;

const CategoriesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  width: 100%;
  min-height: calc(100vh - 250px);
  padding: 1rem 0;
  animation: fadeInUp 0.5s ease;
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
  gap: 2rem;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const CategoryCard = styled.div`
  background: white;
  border-radius: 25px;
  padding: 2rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  text-align: center;
  width: 100%;
  backdrop-filter: blur(10px);
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 20%, rgba(37, 99, 128, 0.03), transparent);
    pointer-events: none;
  }
  
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${colors.lightBlue}, ${colors.darkBlue});
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 30px -12px rgba(0, 0, 0, 0.15);
    border-color: rgba(37, 99, 128, 0.2);
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const CategoryIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(37, 99, 128, 0.1) 0%, rgba(37, 99, 128, 0.05) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  transition: all 0.3s ease;
  
  svg {
    font-size: 40px;
    color: ${colors.lightBlue};
    transition: all 0.3s ease;
  }
  
  ${CategoryCard}:hover & {
    background: linear-gradient(135deg, ${colors.lightBlue} 0%, ${colors.darkBlue} 100%);
    transform: scale(1.05);
    
    svg {
      color: white;
      transform: scale(1.1);
    }
  }
`;

const CategoryTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${colors.darkBlue};
  margin-bottom: 0.75rem;
`;

const CategoryCount = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, rgba(37, 99, 128, 0.1), rgba(37, 99, 128, 0.05));
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${colors.lightBlue};
  margin-top: 0.5rem;
`;

const ScrollableContent = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.5rem;
  max-height: calc(100vh - 200px);
  animation: fadeIn 0.3s ease;

  // No mobile a página inteira já rola: evita scroll aninhado
  @media (max-width: ${breakpoints.mobile}) {
    max-height: none;
    overflow-y: visible;
    padding-right: 0;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, ${colors.lightBlue}, ${colors.darkBlue});
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.darkBlue};
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const PedidosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
  gap: 1.5rem;
  padding-bottom: 1rem;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const PedidoCard = styled.div`
  background: white;
  border-radius: 25px;
  padding: 1.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 100% 0%, rgba(37, 99, 128, 0.03), transparent);
    pointer-events: none;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${colors.lightBlue}, ${colors.darkBlue});
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 30px -12px rgba(0, 0, 0, 0.15);
    border-color: rgba(37, 99, 128, 0.2);
    
    &::before {
      transform: scaleX(1);
    }
    
    .arrow-icon {
      transform: translateX(4px);
      opacity: 1;
    }
  }
`;

const PedidoIconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(37, 99, 128, 0.1) 0%, rgba(37, 99, 128, 0.05) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
  transition: all 0.3s ease;
  
  svg {
    font-size: 28px;
    color: ${colors.lightBlue};
    transition: all 0.3s ease;
  }
  
  ${PedidoCard}:hover & {
    background: linear-gradient(135deg, ${colors.lightBlue} 0%, ${colors.darkBlue} 100%);
    
    svg {
      color: white;
      transform: scale(1.1);
    }
  }
`;

const PedidoTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${colors.darkBlue};
  margin-bottom: 0.5rem;
  line-height: 1.3;
`;

const PedidoDescription = styled.p`
  font-size: 0.85rem;
  color: ${colors.textMuted};
  line-height: 1.5;
  margin-bottom: 1rem;
`;

const PedidoMeta = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Chip = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.3rem 0.7rem;
  border-radius: 100px;
  white-space: nowrap;

  &.price {
    background: rgba(124, 176, 235, 0.15);
    color: ${colors.darkBlue};
  }

  &.docs {
    background: ${colors.backgroundLight};
    color: ${colors.textMuted};
    border: 1px solid ${colors.borderLight};
  }
`;

const PedidoFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
`;

const ArrowIcon = styled(FaChevronRight)`
  font-size: 0.875rem;
  color: ${colors.lightBlue};
  opacity: 0.6;
  transition: all 0.3s ease;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(0, 0, 0, 0.05);
  
  h2 {
    font-size: 1.3rem;
    font-weight: 600;
    color: ${colors.darkBlue};
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    &::before {
      content: '📋';
      font-size: 1.2rem;
    }
  }
  
  button {
    background: none;
    border: none;
    color: ${colors.lightBlue};
    cursor: pointer;
    font-size: 0.85rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 0.3rem;
    min-height: 40px;
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
      color: ${colors.darkBlue};
      transform: translateX(-2px);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 25px;
  border: 2px dashed #e1e4e8;
  
  svg {
    font-size: 4rem;
    color: ${colors.lightBlue};
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  p {
    color: ${colors.textMuted};
    font-size: 1rem;
  }
`;

const NoResultsState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  background: white;
  border-radius: 25px;
  
  p {
    color: ${colors.textMuted};
    font-size: 0.95rem;
  }
  
  span {
    color: ${colors.lightBlue};
    font-weight: 600;
  }
`;

// Um pedido cai numa categoria se declarar `categoria` explicitamente no seu config
// (preferível para pedidos novos). Pedidos antigos, sem essa propriedade, continuam
// sendo classificados pelas palavras-chave do nome, como sempre foi. Se `categoria` for
// um valor que não corresponde a nenhuma categoria conhecida (erro de digitação), cai de
// volta para as palavras-chave em vez de o pedido simplesmente sumir da tela.
// `categoria` também aceita um array (ex: ['inscricao', 'estagio']) para os poucos
// pedidos que fazem sentido em mais de uma categoria ao mesmo tempo, sem duplicar config.
const CATEGORIAS_VALIDAS = ['inscricao', 'pedidos', 'sociedades', 'estagio'];

const pertenceACategoria = (pedido, categoriaId, palavrasChave) => {
  const categoriasDoPedido = Array.isArray(pedido.categoria) ? pedido.categoria : [pedido.categoria];
  const temCategoriaValida = categoriasDoPedido.some(c => CATEGORIAS_VALIDAS.includes(c));

  if (temCategoriaValida) {
    return categoriasDoPedido.includes(categoriaId);
  }

  const nome = pedido.nome.toLowerCase();
  return palavrasChave.some(palavra => nome.includes(palavra));
};

const buscaCasaCom = (pedido, termo) => {
  const termoLower = termo.toLowerCase();
  return pedido.nome.toLowerCase().includes(termoLower) || pedido.descricao.toLowerCase().includes(termoLower);
};

const SelecaoPedidoPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const departamentoId = 'selecao-inscricao';
  const contextoAcesso = useMemo(() => getContextoAcesso(user, authService.getOABData()), [user]);
  const todosPedidos = useMemo(
    () => getPedidosByDepartamento(departamentoId).filter(p => isPedidoVisivelParaUsuario(p, contextoAcesso)),
    [contextoAcesso]
  );
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleBackToHome = () => {
    navigate('/home');
  };

  const categorias = useMemo(() => ({
    inscricao: {
      nome: "Pedidos de Advogados",
      descricao: "Inscrição, reativação, licenciamento e demais atos da advocacia",
      icon: <FaClipboardList />,
      pedidos: todosPedidos.filter(p =>
        pertenceACategoria(p, 'inscricao', ['inscrição', 'matrícula', 'vestibular'])
      )
    },
    estagio: {
      nome: "Pedidos de Estagiário",
      descricao: "Inscrição e demais pedidos de estagiários",
      icon: <FaGraduationCap />,
      pedidos: todosPedidos.filter(p =>
        pertenceACategoria(p, 'estagio', ['estágio', 'estagiário'])
      )
    },
    sociedades: {
      nome: "Pedidos de Sociedade",
      descricao: "Gestão de sociedades e associações",
      icon: <FaUsers />,
      pedidos: todosPedidos.filter(p =>
        pertenceACategoria(p, 'sociedades', ['sociedade', 'associação', 'cooperativa'])
      )
    },
    pedidos: {
      nome: "Pedidos Gerais",
      descricao: "Pedido de baixa, licenciamento e outros serviços",
      icon: <FaFileContract />,
      pedidos: todosPedidos.filter(p =>
        pertenceACategoria(p, 'pedidos', ['pedido', 'baixa', 'licenciamento', 'trancamento'])
      )
    }
  }), [todosPedidos]);

  // Busca dentro da categoria aberta; sem categoria aberta, busca em todos os pedidos.
  const filteredPedidos = useMemo(() => {
    const base = selectedCategory ? categorias[selectedCategory].pedidos : todosPedidos;
    if (!searchTerm) return selectedCategory ? base : [];
    return base.filter(pedido => buscaCasaCom(pedido, searchTerm));
  }, [selectedCategory, categorias, todosPedidos, searchTerm]);

  const currentCategory = selectedCategory ? categorias[selectedCategory] : null;
  const buscandoSemCategoria = !selectedCategory && searchTerm.length > 0;

  if (todosPedidos.length === 0) {
    return (
      <MainLayout>
        <PageContainer>
          <Container>
            <TopBar>
              <LeftSection>
                <BackButton onClick={handleBackToHome}>
                  <FaArrowLeft /> Voltar
                </BackButton>
              </LeftSection>
            </TopBar>
            <EmptyState>
              <FaFileAlt />
              <p>Nenhum requerimento disponível para o seu perfil no momento</p>
            </EmptyState>
          </Container>
        </PageContainer>
      </MainLayout>
    );
  }

  const mostrandoListaDePedidos = selectedCategory || buscandoSemCategoria;

  const statsLabel = selectedCategory
    ? currentCategory?.nome
    : (buscandoSemCategoria ? 'Resultados da busca' : 'Total disponível');
  const statsCount = selectedCategory ? filteredPedidos.length : (buscandoSemCategoria ? filteredPedidos.length : todosPedidos.length);

  const voltarDaLista = () => {
    setSelectedCategory(null);
    setSearchTerm('');
  };

  return (
    <MainLayout>
      <PageContainer>
        <Container>
          <TopBar>
            <LeftSection>
              {!selectedCategory && (
                <BackButton onClick={handleBackToHome}>
                  <FaArrowLeft /> Voltar ao Início
                </BackButton>
              )}
              <StatsBadge>
                <span>{statsLabel}</span>
                <span className="count">{statsCount}</span>
              </StatsBadge>
            </LeftSection>
            <SearchContainer>
              <SearchIcon />
              <SearchInput
                type="text"
                placeholder={selectedCategory ? "Buscar nesta categoria..." : "Buscar em todos os pedidos..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchContainer>
          </TopBar>

          {!mostrandoListaDePedidos ? (
            <CategoriesWrapper>
              <CategoriesGrid>
                {Object.entries(categorias).filter(([, categoria]) => categoria.pedidos.length > 0).map(([chave, categoria]) => (
                  <CategoryCard key={chave} onClick={() => setSelectedCategory(chave)}>
                    <CategoryIcon>{categoria.icon}</CategoryIcon>
                    <CategoryTitle>{categoria.nome}</CategoryTitle>
                    <CategoryCount>{categoria.pedidos.length} pedidos</CategoryCount>
                  </CategoryCard>
                ))}
              </CategoriesGrid>
            </CategoriesWrapper>
          ) : (
            <ScrollableContent>
              <SectionHeader>
                <h2>{selectedCategory ? currentCategory?.nome : 'Resultados da busca'}</h2>
                <button onClick={voltarDaLista}>
                  <FaArrowLeft /> Voltar para categorias
                </button>
              </SectionHeader>

              {filteredPedidos.length === 0 ? (
                <NoResultsState>
                  <p>Nenhum requerimento encontrado para <span>"{searchTerm}"</span></p>
                </NoResultsState>
              ) : (
                <PedidosGrid>
                  {filteredPedidos.map((pedido, index) => (
                    <PedidoCard
                      key={pedido.id}
                      onClick={() => navigate(`/requerimentos/${departamentoId}/${pedido.id}`)}
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <PedidoIconWrapper>
                        <FaFileAlt />
                      </PedidoIconWrapper>
                      <PedidoTitle>{pedido.nome}</PedidoTitle>
                      <PedidoDescription>{pedido.descricao}</PedidoDescription>
                      <PedidoMeta>
                        <Chip className="price">
                          {pedido.precoBase === 0
                            ? 'Sem Taxa'
                            : `${pedido.precoVariavel ? 'a partir de ' : ''}${formatCurrency(pedido.precoBase)}`}
                        </Chip>
                        <Chip className="docs">
                          {pedido.totalDocumentos} documento{pedido.totalDocumentos === 1 ? '' : 's'}
                        </Chip>
                      </PedidoMeta>
                      <PedidoFooter>
                        <ArrowIcon className="arrow-icon" />
                      </PedidoFooter>
                    </PedidoCard>
                  ))}
                </PedidosGrid>
              )}
            </ScrollableContent>
          )}
        </Container>
      </PageContainer>
    </MainLayout>
  );
};

export default SelecaoPedidoPage;